/* eslint-disable restrict-imports/restrict-imports */
import { Popper } from '@material-ui/core';
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import { Icon, Button } from '@scale/scaleui';
import _ from 'lodash';
import {
  ResponseTemplateStep,
  ResponseTemplatePopperMetadata,
  escapeModelId,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplateCreator/ResponseTemplateCreatorStep';
import { ResponseTemplatePath } from './ResponseTemplateCreator/ResponseTemplatePath';
import { IndexedInteractionStepResponse } from '@scale/shared/types/chat';
import { useInlineResponseTemplatingContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/InlineResponseTemplatingContext';

interface ResponseTemplatingPopperProps {
  childId?: string;
  metadata: ResponseTemplatePopperMetadata;
  options?: {
    disablePortal?: boolean;
  };
}

const ResponseTemplatingPopper: React.FC<ResponseTemplatingPopperProps> = ({
  childId,
  metadata,
  options = { disablePortal: true },
}) => {
  const { stepIndex, candidateId, fieldId, numberFieldScale } = metadata || {};
  const { disablePortal } = options;

  const [open, setOpen] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !popoverRef.current.outerHTML.includes(event.target.outerHTML)
      ) {
        setOpen(false);
        // Reset response
        setStepTemplatedResponse(response);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const {
    stepsWithResponses,
    history,
    setResponseForIndex,
    updateResponseMiddleware,
    getVariables,
  } = useInlineResponseTemplatingContext();

  const [step, response] = stepsWithResponses[stepIndex ?? 0] ?? [];
  const templateVariables = getVariables();
  const templatePaths: ResponseTemplatePath[] = useMemo(() => {
    return history && step ? step.getResponseTemplatePaths(history.until(stepIndex ?? 0)) : [];
  }, [history, step, stepIndex]);

  const [stepTemplatedResponse, setStepTemplatedResponse] = useState<
    IndexedInteractionStepResponse | undefined
  >(response);

  useEffect(() => {
    if (!_.isEqual(response, stepTemplatedResponse)) {
      setStepTemplatedResponse(response);
    }
  }, [response]);

  // Filter paths to only those that are relevant to this candidateId and/or fieldId
  const filteredPaths = useMemo(() => {
    let paths = templatePaths;
    if (candidateId) {
      // Filter if path.path includes candidateId, and it's wrapped by [" and "]
      paths = paths.filter(path => path.path.includes(escapeModelId(candidateId)));
    }
    if (fieldId) {
      // Filter if path.path includes candidateId, and it's surrounded by . and . (meaning it's an entire fieldId)
      paths = paths.filter(path => path.path.includes(`.${fieldId}.`));
    }
    return paths;
  }, [candidateId, fieldId, templatePaths]);

  const updateEditedResponse = useCallback(
    (path: string, value: any) => {
      if (!stepTemplatedResponse) return;
      const newStepResponse = _.cloneDeep(stepTemplatedResponse);
      _.set(newStepResponse, path, value);
      updateResponseMiddleware(newStepResponse, metadata, path, value);
      setStepTemplatedResponse(newStepResponse);
    },
    [metadata, stepTemplatedResponse, updateResponseMiddleware],
  );

  const saveTemplatedResponse = useCallback(() => {
    // Triggers upstream response change + population + propagation
    if (stepIndex !== undefined) {
      setResponseForIndex(stepTemplatedResponse!, stepIndex);
    }
  }, [setResponseForIndex, stepIndex, stepTemplatedResponse]);

  // Whether each path has a value
  const allMapped = useMemo(() => {
    return filteredPaths.every(path => {
      return !_.isNil(_.get(response, path.path)) && _.get(response, path.path) !== '';
    });
  }, [filteredPaths, response]);

  if (
    !childId ||
    stepIndex === undefined ||
    !stepTemplatedResponse ||
    !filteredPaths?.length ||
    !templateVariables
  )
    return null;

  return (
    <>
      <div ref={iconRef}>
        <Icon
          icon={['far', 'exchange-alt']}
          size="md"
          variant={allMapped ? 'neutral' : 'danger'}
          tooltip={allMapped ? 'Edit mapping for this response' : 'Fields are missing mappings'}
          onClick={() => {
            setAnchorEl(iconRef.current);
            setOpen(!open);
          }}
          hasBackground={true}
          selected={open}
        />
      </div>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="right-start"
        disablePortal={disablePortal}
        style={{ zIndex: 1500 }}
        className="scaleui scale-tailwind scale-theme"
      >
        <Transition
          show={open}
          appear
          enter="transition-all duration-300"
          enterFrom="opacity-0 -translate-y-1"
          enterTo="opacity-100 translate-y-0 "
          leave="transition-all duration-300"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-1"
          as="div"
          ref={popoverRef}
          className={`text-neutral-700 border border-neutral-200 shadow-lg text-left rounded-lg h-fit bg-neutral-0 flex flex-col w-96 mx-6 overflow-visible`}
        >
          <div className="scaleui scale-tailwind scale-theme p-4 space-y-4 max-h-96 overflow-scroll">
            {filteredPaths && templateVariables && (
              <ResponseTemplateStep
                key={`step-${step.params.id}-${stepIndex}-${candidateId}-${fieldId}`}
                responseIndex={stepIndex}
                title={`Step ${stepIndex} - ${step?.title}`}
                subtitle={step?.params.title ?? ''}
                paths={filteredPaths}
                response={stepTemplatedResponse}
                updateResponse={updateEditedResponse}
                variables={templateVariables}
                isPopper
                numberFieldScale={
                  candidateId && numberFieldScale
                    ? { ...numberFieldScale, scaleToDataBank: false }
                    : undefined
                }
              />
            )}
            <Button variant="primary" onClick={saveTemplatedResponse}>
              Save
            </Button>
          </div>
        </Transition>
      </Popper>
    </>
  );
};

export default ResponseTemplatingPopper;
